import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Mail,
  RefreshCw,
  ArrowLeft,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Calendar,
  MapPin,
  User,
  Save,
  Shield,
  PlugZap,
  Download,
  Trash2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaultForm = {
  provider: 'outlook',
  email_address: '',
  imap_server: 'outlook.office365.com',
  imap_port: 993,
  smtp_server: 'smtp.office365.com',
  smtp_port: 587,
  tenant_id: '',
  client_id: '',
  client_secret: '',
  app_password: ''
};

export default function EmailSettings() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [formData, setFormData] = useState(defaultForm);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testingConfig, setTestingConfig] = useState(false);
  const [hasStoredSecret, setHasStoredSecret] = useState(false);
  const [recentEmails, setRecentEmails] = useState([]);
  const [loadingRecentEmails, setLoadingRecentEmails] = useState(false);
  const [recentEmailsExpanded, setRecentEmailsExpanded] = useState(false);
  const [reprocessingEmailIds, setReprocessingEmailIds] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statusRes, logsRes, configRes] = await Promise.all([
        axios.get(`${API}/email/status`),
        axios.get(`${API}/email/logs`),
        axios.get(`${API}/email/config`)
      ]);

      setStatus(statusRes.data);
      setLogs(logsRes.data);
      setHasStoredSecret(Boolean(configRes.data?.has_secret));
      setFormData({
        provider: configRes.data?.provider || 'outlook',
        email_address: configRes.data?.email_address || '',
        imap_server: configRes.data?.imap_server || 'outlook.office365.com',
        imap_port: configRes.data?.imap_port || 993,
        smtp_server: configRes.data?.smtp_server || 'smtp.office365.com',
        smtp_port: configRes.data?.smtp_port || 587,
        tenant_id: configRes.data?.tenant_id || '',
        client_id: configRes.data?.client_id || '',
        client_secret: '',
        app_password: ''
      });
      await fetchRecentEmails();
    } catch (error) {
      toast.error('Erro ao carregar dados do serviço de email');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentEmails = async () => {
    setLoadingRecentEmails(true);
    try {
      const response = await axios.get(`${API}/email/recent`);
      setRecentEmails(response.data?.emails || []);
    } catch (error) {
      setRecentEmails([]);
    } finally {
      setLoadingRecentEmails(false);
    }
  };

  const handleDeleteRecentEmail = async (emailId) => {
    const confirmed = window.confirm('Apagar este email da caixa de entrada?');
    if (!confirmed) {
      return;
    }

    try {
      await axios.delete(`${API}/email/recent/${emailId}`);
      toast.success('Email apagado da caixa de entrada');
      await fetchRecentEmails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível apagar o email');
    }
  };

  const updateProviderDefaults = (provider) => {
    if (provider === 'outlook') {
      setFormData((prev) => ({
        ...prev,
        provider,
        imap_server: 'outlook.office365.com',
        imap_port: 993,
        smtp_server: 'smtp.office365.com',
        smtp_port: 587,
        app_password: ''
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      provider,
      imap_server: 'imap.zoho.com',
      imap_port: 993,
      smtp_server: 'smtp.zoho.com',
      smtp_port: 587,
      tenant_id: '',
      client_id: '',
      client_secret: ''
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const buildPayload = () => ({
    ...formData,
    imap_port: Number(formData.imap_port),
    smtp_port: Number(formData.smtp_port)
  });

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await axios.put(`${API}/email/config`, buildPayload());
      toast.success(response.data.message || 'Configuração salva com sucesso');
      setHasStoredSecret(Boolean(response.data?.config?.has_secret));
      setFormData((prev) => ({
        ...prev,
        client_secret: '',
        app_password: ''
      }));
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConfig = async () => {
    setTestingConfig(true);
    try {
      const response = await axios.post(`${API}/email/test`, buildPayload());
      if (response.data?.success) {
        toast.success(response.data.message || 'Conexão validada com sucesso');
      } else {
        toast.error(response.data?.message || 'Falha no teste de conexão');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao testar configuração');
    } finally {
      setTestingConfig(false);
    }
  };

  const handleCheckNow = async () => {
    setChecking(true);
    try {
      await axios.post(`${API}/email/check-now`);
      toast.success('Verificação de emails iniciada');
      setTimeout(() => {
        fetchData();
        setChecking(false);
      }, 5000);
    } catch (error) {
      toast.error('Erro ao verificar emails');
      setChecking(false);
    }
  };

  const handleDownloadRawEmail = async (emailId) => {
    try {
      const response = await axios.get(`${API}/email/recent/${emailId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'message/rfc822' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `email-${emailId}.eml`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Não foi possível baixar o email bruto');
    }
  };

  const handleReprocessEmail = async (emailId) => {
    setReprocessingEmailIds((prev) => ({ ...prev, [emailId]: true }));
    try {
      const response = await axios.post(`${API}/email/reprocess/${emailId}`);
      toast.success(response.data?.message || 'Solicitação enviada para reprocessamento');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível reprocessar a solicitação');
    } finally {
      setReprocessingEmailIds((prev) => {
        const next = { ...prev };
        delete next[emailId];
        return next;
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const getLogPresentation = (statusValue) => {
    if (statusValue === 'confirmed' || statusValue === 'confirmed_manual') {
      return {
        icon: <CheckCircle className="w-5 h-5 text-green-600" />,
        iconClass: 'bg-green-100',
        badgeClass: 'bg-green-100 text-green-700',
        label: statusValue === 'confirmed_manual' ? 'Manual' : 'Confirmado'
      };
    }

    if (statusValue === 'ignored') {
      return {
        icon: <AlertCircle className="w-5 h-5 text-amber-600" />,
        iconClass: 'bg-amber-100',
        badgeClass: 'bg-amber-100 text-amber-700',
        label: 'Ignorado'
      };
    }

    if (statusValue === 'error') {
      return {
        icon: <AlertCircle className="w-5 h-5 text-orange-600" />,
        iconClass: 'bg-orange-100',
        badgeClass: 'bg-orange-100 text-orange-700',
        label: 'Erro'
      };
    }

    if (statusValue === 'pending_approval') {
      return {
        icon: <Shield className="w-5 h-5 text-blue-600" />,
        iconClass: 'bg-blue-100',
        badgeClass: 'bg-blue-100 text-blue-700',
        label: 'Pendente'
      };
    }

    return {
      icon: <XCircle className="w-5 h-5 text-red-600" />,
      iconClass: 'bg-red-100',
      badgeClass: 'bg-red-100 text-red-700',
      label: 'Rejeitado'
    };
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/40 bg-white/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-button"
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              size="sm"
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
                <Mail className="w-5 h-5 text-primary-foreground" />
              </div>
              <h1 className="text-xl font-manrope font-bold tracking-tight">
                Configuração de Email
              </h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-12 space-y-8">
        <div className="bg-white rounded-2xl border border-border/40 p-6 shadow-sm">
          <h2 className="text-lg font-manrope font-bold mb-6 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-primary" />
            Status do Serviço
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
              {status?.email_configured ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <XCircle className="w-5 h-5 text-red-500" />
              )}
              <div>
                <p className="text-sm text-muted-foreground">Configuração Válida</p>
                <p className="font-medium">{status?.email_configured ? 'Sim' : 'Não'}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
              <PlugZap className="w-5 h-5 text-blue-500" />
              <div>
                <p className="text-sm text-muted-foreground">Provider</p>
                <p className="font-medium uppercase">{status?.provider || '-'}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
              <Shield className="w-5 h-5 text-amber-500" />
              <div>
                <p className="text-sm text-muted-foreground">Autenticação</p>
                <p className="font-medium uppercase">{status?.auth_mode || '-'}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
              <Mail className="w-5 h-5 text-purple-500" />
              <div>
                <p className="text-sm text-muted-foreground">Email Monitorado</p>
                <p className="font-medium text-sm truncate" title={status?.email_address}>
                  {status?.email_address}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
              <Calendar className="w-5 h-5 text-emerald-500" />
              <div>
                <p className="text-sm text-muted-foreground">Fluxo OWA / Graph</p>
                <p className="font-medium">{status?.graph_sync_available ? 'Ativo' : 'Nao configurado'}</p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-4">
            <Button
              data-testid="test-email-config-button"
              onClick={handleTestConfig}
              disabled={testingConfig}
              variant="outline"
              className="rounded-full"
            >
              <PlugZap className={`w-4 h-4 mr-2 ${testingConfig ? 'animate-pulse' : ''}`} />
              {testingConfig ? 'Testando...' : 'Testar Configuração'}
            </Button>

            <Button
              data-testid="check-emails-button"
              onClick={handleCheckNow}
              disabled={checking}
              className="rounded-full"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${checking ? 'animate-spin' : ''}`} />
              {checking ? 'Verificando...' : 'Verificar Emails Agora'}
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-border/40 p-6 shadow-sm">
          <h2 className="text-lg font-manrope font-bold mb-6 flex items-center gap-2">
            <SettingsIcon />
            Configuração da Caixa de Entrada
          </h2>

          <form onSubmit={handleSave} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Provider</Label>
                <Select value={formData.provider} onValueChange={updateProviderDefaults}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o provider" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="outlook">Outlook / Microsoft 365</SelectItem>
                    <SelectItem value="zoho">Zoho Mail</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email_address">Email monitorado</Label>
                <Input
                  id="email_address"
                  type="email"
                  value={formData.email_address}
                  onChange={(e) => handleInputChange('email_address', e.target.value)}
                  placeholder="reuniao@empresa.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="imap_server">Servidor IMAP</Label>
                <Input
                  id="imap_server"
                  value={formData.imap_server}
                  onChange={(e) => handleInputChange('imap_server', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="imap_port">Porta IMAP</Label>
                <Input
                  id="imap_port"
                  type="number"
                  value={formData.imap_port}
                  onChange={(e) => handleInputChange('imap_port', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="smtp_server">Servidor SMTP</Label>
                <Input
                  id="smtp_server"
                  value={formData.smtp_server}
                  onChange={(e) => handleInputChange('smtp_server', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="smtp_port">Porta SMTP</Label>
                <Input
                  id="smtp_port"
                  type="number"
                  value={formData.smtp_port}
                  onChange={(e) => handleInputChange('smtp_port', e.target.value)}
                />
              </div>
            </div>

            {formData.provider === 'outlook' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="tenant_id">Tenant ID</Label>
                  <Input
                    id="tenant_id"
                    value={formData.tenant_id}
                    onChange={(e) => handleInputChange('tenant_id', e.target.value)}
                    placeholder="Microsoft Entra tenant ID"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="client_id">Client ID</Label>
                  <Input
                    id="client_id"
                    value={formData.client_id}
                    onChange={(e) => handleInputChange('client_id', e.target.value)}
                    placeholder="Application client ID"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="client_secret">Client Secret</Label>
                  <Input
                    id="client_secret"
                    type="password"
                    value={formData.client_secret}
                    onChange={(e) => handleInputChange('client_secret', e.target.value)}
                    placeholder={hasStoredSecret ? 'Deixe em branco para manter o secret atual' : 'Cole o client secret'}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="app_password">Senha de app</Label>
                <Input
                  id="app_password"
                  type="password"
                  value={formData.app_password}
                  onChange={(e) => handleInputChange('app_password', e.target.value)}
                  placeholder={hasStoredSecret ? 'Deixe em branco para manter a senha atual' : 'Informe a senha de app'}
                />
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 text-sm text-blue-900 space-y-2">
              <p>
                A configuração é salva no backend e aplicada imediatamente ao serviço de leitura/envio de emails.
              </p>
              <p>
                Segredos não voltam para o frontend. Se o campo de secret/senha ficar em branco, o backend mantém o valor já salvo.
              </p>
            </div>

            {formData.provider === 'outlook' && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 text-sm text-emerald-950 space-y-2">
                <p className="font-semibold">Procedimento OWA / Outlook Web</p>
                <p>
                  Se a mailbox receber o convite como link OWA, sem anexo .ics, o backend tenta localizar o mesmo evento pelo calendario da conta monitorada via Microsoft Graph.
                </p>
                <p>1. Use aqui o mesmo Tenant ID, Client ID e Client Secret do app registrado no Microsoft Entra.</p>
                <p>2. No app do Entra, adicione permissao de Application em Microsoft Graph: <strong>Calendars.ReadBasic</strong> ou <strong>Calendars.Read</strong>.</p>
                <p>3. Clique em <strong>Grant admin consent</strong> para a permissao de Graph.</p>
                <p>4. Para forcar a entrega .ics quando possivel, ajuste a mailbox monitorada no Exchange Online:</p>
                <pre className="rounded-xl bg-white border border-emerald-200 p-3 overflow-x-auto text-xs">
{`Set-CASMailbox reuniao@tecnoservicebrasil.com.br \\
  -ImapUseProtocolDefaults $false \\
  -ImapForceICalForCalendarRetrievalOption $true`}
                </pre>
                <p>5. Depois de salvar aqui, use <strong>Testar Configuracao</strong> e em seguida <strong>Verificar Emails Agora</strong>.</p>
              </div>
            )}

            <div className="flex flex-wrap gap-4">
              <Button type="submit" disabled={saving} className="rounded-full">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Salvando...' : 'Salvar Configuração'}
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={handleTestConfig}
                disabled={testingConfig}
                className="rounded-full"
              >
                <PlugZap className="w-4 h-4 mr-2" />
                Testar sem salvar
              </Button>
            </div>
          </form>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
          <h3 className="font-manrope font-bold text-blue-900 mb-3 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Como funciona o agendamento por email
          </h3>
          <div className="text-sm text-blue-800 space-y-2">
            <p>1. Envie um convite de reunião (.ics) para: <strong>{status?.email_address}</strong></p>
            <p>2. No campo <strong>Local</strong> do convite, coloque o nome exato da sala</p>
            <p>3. O sistema verifica a caixa de entrada a cada <strong>{status?.check_interval_minutes || 1} minuto(s)</strong></p>
            <p>4. Se a sala estiver disponível, a reunião será criada automaticamente</p>
            <p>5. Se houver conflito, o sistema envia resposta com rejeição ou horários alternativos</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-border/40 p-6 shadow-sm">
          <div className="flex items-center justify-between gap-4 mb-6">
            <h2 className="text-lg font-manrope font-bold flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              Diagnóstico da Caixa Monitorada
            </h2>
            <div className="flex flex-wrap justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setRecentEmailsExpanded((prev) => !prev)}
                className="rounded-full"
              >
                {recentEmailsExpanded ? (
                  <ChevronUp className="w-4 h-4 mr-2" />
                ) : (
                  <ChevronDown className="w-4 h-4 mr-2" />
                )}
                {recentEmailsExpanded ? 'Recolher' : 'Expandir'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={fetchRecentEmails}
                disabled={loadingRecentEmails}
                className="rounded-full"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingRecentEmails ? 'animate-spin' : ''}`} />
                {loadingRecentEmails ? 'Atualizando...' : 'Atualizar Emails'}
              </Button>
            </div>
          </div>

          {!recentEmailsExpanded ? (
            <div className="text-sm text-muted-foreground">
              <p>A seção de diagnóstico está recolhida. Expanda para ver os emails recentes e baixar o .eml bruto.</p>
            </div>
          ) : recentEmails.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>Nenhum email recente encontrado para diagnóstico</p>
            </div>
          ) : (
            <div className="space-y-4">
              {recentEmails.map((emailItem) => (
                <div key={emailItem.email_id} className="p-4 bg-gray-50 rounded-xl border border-border/30">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <h4 className="font-medium truncate">{emailItem.subject}</h4>
                      <div className="mt-1 text-sm text-muted-foreground space-y-1">
                        <p>Remetente: {emailItem.from_email || '-'}</p>
                        <p>Cc: {emailItem.cc || '-'}</p>
                        <p>Email ID: {emailItem.email_id}</p>
                        <p>Status de processamento: {emailItem.processing_status || 'pendente'}</p>
                        <p>ICS detectado: {emailItem.has_ics ? 'Sim' : 'Não'}</p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-full shrink-0"
                      onClick={() => handleDownloadRawEmail(emailItem.email_id)}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Baixar .eml
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-full shrink-0 text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => handleDeleteRecentEmail(emailItem.email_id)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Apagar email
                    </Button>
                    {!emailItem.has_ics && (
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-full shrink-0"
                        onClick={() => navigate(`/email/manual/${emailItem.email_id}`)}
                      >
                        Criar manualmente
                      </Button>
                    )}
                  </div>
                  {emailItem.debug && (
                    <pre className="mt-3 text-xs bg-white border border-border/40 rounded-xl p-3 overflow-x-auto whitespace-pre-wrap">
                      {JSON.stringify(emailItem.debug, null, 2)}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-border/40 p-6 shadow-sm">
          <h2 className="text-lg font-manrope font-bold mb-6 flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            Histórico de Agendamentos por Email
          </h2>

          {logs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Mail className="w-12 h-12 mx-auto mb-4 opacity-20" />
              <p>Nenhum agendamento por email foi processado ainda</p>
            </div>
          ) : (
            <div className="space-y-4">
              {logs.map((log) => (
                <div
                  key={log.id}
                  className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl"
                >
                  {(() => {
                    const presentation = getLogPresentation(log.status);
                    return (
                      <>
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${presentation.iconClass}`}>
                          {presentation.icon}
                        </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium truncate">{log.event_summary}</h4>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {log.room_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {log.from_email}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {format(new Date(log.processed_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                      </span>
                    </div>
                    {log.status === 'rejected' && log.reason && (
                      <p className="text-sm text-red-600 mt-1">Motivo: {log.reason}</p>
                    )}
                    {log.status !== 'rejected' && log.reason && (
                      <p className="text-sm text-muted-foreground mt-1">Detalhe: {log.reason}</p>
                    )}
                  </div>
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${presentation.badgeClass}`}>
                          {presentation.label}
                        </span>
                        <div className="shrink-0">
                          <Button
                            type="button"
                            variant="outline"
                            className="rounded-full"
                            disabled={!log.email_id || Boolean(reprocessingEmailIds[log.email_id])}
                            onClick={() => handleReprocessEmail(log.email_id)}
                          >
                            <RefreshCw className={`w-4 h-4 mr-2 ${reprocessingEmailIds[log.email_id] ? 'animate-spin' : ''}`} />
                            Reprocessar
                          </Button>
                        </div>
                      </>
                    );
                  })()}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function SettingsIcon() {
  return <Shield className="w-5 h-5 text-primary" />;
}
